import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db, CAMPAIGN_ID } from '../../firebase';
import { useCampaign } from '../../contexts/CampaignContext';
import { summarize } from '../../lib/characterIO';

// DM-side modal listing the last N snapshots for a single player. The DM
// can pick one to restore. Snapshots come from
// `campaigns/{cid}/playerBackups/{playerId}/snapshots`.
export default function PlayerHistoryPanel({ player, onClose }) {
  const { restorePlayerSnapshot } = useCampaign();
  const [loading, setLoading] = useState(true);
  const [snaps, setSnaps]     = useState([]);
  const [error, setError]     = useState(null);
  const [busyId, setBusyId]   = useState(null);

  useEffect(() => {
    if (!player?.id) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const q = query(
          collection(db, 'campaigns', CAMPAIGN_ID, 'playerBackups', player.id, 'snapshots'),
          orderBy('at', 'desc'),
        );
        const result = await getDocs(q);
        if (cancelled) return;
        setSnaps(result.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load history.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [player?.id]);

  const restore = async (snap) => {
    if (!confirm(`Restore ${summarize(snap.record)} from ${new Date(snap.at).toLocaleString()}? This overwrites the current player record.`)) return;
    try {
      setBusyId(snap.id);
      await restorePlayerSnapshot(player.id, snap.id);
      onClose?.();
    } catch (err) {
      setError(err.message || 'Restore failed.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={styles.panel}>
        <div style={styles.header}>
          <span style={styles.title}>🔁 History — {player?.charName || player?.name}</span>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={styles.body}>
          {loading && <div style={styles.empty}>Loading…</div>}
          {error && <div style={styles.error}>{error}</div>}
          {!loading && !error && snaps.length === 0 && (
            <div style={styles.empty}>No snapshots yet. They appear automatically a few seconds after each character edit.</div>
          )}
          {snaps.map((s) => (
            <div key={s.id} style={styles.row}>
              <div style={styles.rowMain}>
                <div style={styles.rowName}>{summarize(s.record)}</div>
                <div style={styles.rowMeta}>{new Date(s.at).toLocaleString()}</div>
              </div>
              <button
                style={styles.restoreBtn}
                disabled={!!busyId}
                onClick={() => restore(s)}
              >
                {busyId === s.id ? 'Restoring…' : 'Restore'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  panel:   { width: '100%', maxWidth: 480, maxHeight: '85vh', background: 'linear-gradient(180deg, #1a2a48, #142038)', border: '2px solid #8b7340', borderRadius: 8, boxShadow: '0 20px 60px rgba(0,0,0,0.7)', display: 'flex', flexDirection: 'column', overflow: 'hidden', color: '#f0e6d0', fontFamily: "'Press Start 2P', monospace" },
  header:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #4a3d28', background: 'rgba(0,0,0,0.2)' },
  title:   { fontSize: 14, fontWeight: 800, color: '#d4a847', letterSpacing: '0.04em' },
  closeBtn:{ background: 'transparent', border: 'none', color: '#9a8b6e', fontSize: 20, cursor: 'pointer', padding: '0 4px' },
  body:    { flex: 1, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 6 },
  empty:   { color: '#6a5d45', fontSize: 13, textAlign: 'center', padding: '12px 8px', fontStyle: 'italic' },
  error:   { color: '#f87171', fontSize: 12, padding: 8, background: 'rgba(192,57,43,0.15)', border: '1px solid rgba(192,57,43,0.5)', borderRadius: 4 },
  row:     { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4 },
  rowMain: { flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  rowName: { fontSize: 13, fontWeight: 700, color: '#f0e6d0' },
  rowMeta: { fontSize: 11, color: '#9a8b6e' },
  restoreBtn: { background: 'rgba(212,168,71,0.15)', border: '1px solid #8b7340', color: '#d4a847', padding: '6px 12px', fontSize: 11, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', borderRadius: 4, letterSpacing: '0.04em' },
};
